import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  DollarSign, 
  Target, 
  Lightbulb, 
  Settings, 
  Search,
  Bell,
  Menu,
  X,
  Rocket,
  Zap,
  MessageCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import NotificationBell from './NotificationBell';

const AppShell = ({ children }) => {
  const location = useLocation();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const navItems = [
    { path: '/chat', label: 'LEE', icon: MessageCircle },
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/ai-command', label: 'AI Command', icon: Target },
    { path: '/autonomous-build', label: 'Auto Build', icon: Zap },
    { path: '/execution', label: 'Execution', icon: Rocket },
    { path: '/wealth', label: 'Wealth', icon: DollarSign },
    { path: '/opportunities', label: 'Opportunities', icon: Lightbulb },
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="h-screen flex flex-col bg-slate-950">
      {/* Top Header - Desktop & Mobile */}
      <header className="sticky top-0 z-50 w-full border-b border-slate-800 bg-slate-900/95 backdrop-blur supports-[backdrop-filter]:bg-slate-900/60">
        <div className="flex h-16 items-center px-4 md:px-6">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="mr-4 md:hidden p-2 hover:bg-slate-800 rounded-lg transition-colors"
            data-testid="mobile-menu-button"
          >
            {mobileSidebarOpen ? (
              <X className="h-5 w-5 text-slate-300" />
            ) : (
              <Menu className="h-5 w-5 text-slate-300" />
            )}
          </button>

          {/* Logo/Title */}
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">L</span>
            </div>
            <span className="font-semibold text-lg text-white hidden sm:inline-block">
              LEE
            </span>
          </div>

          {/* Global Search - Desktop */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                data-testid="global-search"
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="ml-auto flex items-center space-x-2">
            <NotificationBell />
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex w-64 flex-col border-r border-slate-800 bg-slate-900">
          <nav className="flex-1 p-4 space-y-1" data-testid="desktop-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors",
                    active
                      ? "bg-amber-500/10 text-amber-500 font-medium"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-slate-800">
            <div className="text-xs text-slate-500">
              Private Intelligence System
            </div>
          </div>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {mobileSidebarOpen && (
          <div 
            className="md:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setMobileSidebarOpen(false)}
          >
            <aside 
              className="fixed left-0 top-16 bottom-16 w-64 bg-slate-900 border-r border-slate-800"
              onClick={(e) => e.stopPropagation()}
            >
              <nav className="p-4 space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileSidebarOpen(false)}
                      className={cn(
                        "flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors",
                        active
                          ? "bg-amber-500/10 text-amber-500 font-medium"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </aside>
          </div>
        )}

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto bg-slate-950">
          <div className="pb-20 md:pb-0">
            {children}
          </div>
        </main>
      </div>

      {/* Bottom Navigation - Mobile Only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-900 border-t border-slate-800 overflow-x-auto" data-testid="mobile-nav">
        <div className="flex h-16 min-w-max">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center justify-center space-y-1 transition-colors flex-1 min-w-[72px] px-2",
                  active
                    ? "text-amber-500"
                    : "text-slate-400 active:bg-slate-800"
                )}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default AppShell;